import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  RiArrowLeftLine,
  RiShieldCrossLine,
  RiShieldCheckLine,
  RiEdit2Line,
  RiCloseCircleLine
} from '@remixicon/react';
import { Button, Card, CardBody, Chip, Spinner } from '@heroui/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import DefaultLayout from '@/layouts/default';
import {
  experimentDetailQueryAtom,
  experimentDetailIdAtom,
  experimentDetailRefreshAtom,
  setExperimentStatus
} from '@/store';
import { UserCell } from '@/components/shared/UserCell';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { DomainCell } from '@/components/shared/DomainCell';
import { OrganizationCell } from '@/components/shared/OrganizationCell';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { BlockExperimentModal } from '@/components/experiments/BlockExperimentModal';
import { UnblockExperimentModal } from '@/components/experiments/UnblockExperimentModal';
import { SetExperimentStatusModal } from '@/components/experiments/SetExperimentStatusModal';
import { CancelExperimentModal } from '@/components/experiments/CancelExperimentModal';
import {
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  isBlockableStatus,
  isCancellableStatus,
  type ExperimentStatus
} from '@/utils/experiments';
import { getTrackerLabel } from '@/utils/trackers';
import { ExperimentTypesBadges } from '@/components/experiments/ExperimentTypesBadges';
import { SelectorReadOnlyView } from '@/components/experiments/SelectorReadOnlyView';
import { JourneyReadOnlyView } from '@/components/experiments/JourneyReadOnlyView';
import { SegmentReadOnlyView } from '@/components/experiments/SegmentReadOnlyView';
import type { ExperimentItem, JourneyWithSteps, BackendSegment } from '@/types';

export default function ExperimentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [experimentId, setExperimentId] = useAtom(experimentDetailIdAtom);
  const refreshDetail = useSetAtom(experimentDetailRefreshAtom);
  const { data, isLoading, isError } = useAtomValue(experimentDetailQueryAtom);
  const [, setStatusAtom] = useAtom(setExperimentStatus);

  const [isBlockModalOpen, setIsBlockModalOpen] = React.useState(false);
  const [isUnblockModalOpen, setIsUnblockModalOpen] = React.useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = React.useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (id) {
      setExperimentId(Number(id));
    }
  }, [id, setExperimentId]);

  const experiment = data?.data;

  const triggerRefresh = React.useCallback(() => {
    refreshDetail((n) => n + 1);
  }, [refreshDetail]);

  const handleStatusUpdate = React.useCallback(
    async (status: ExperimentStatus) => {
      if (!experiment) return;
      await setStatusAtom({ id: experiment.id, status });
      triggerRefresh();
    },
    [experiment, setStatusAtom, triggerRefresh]
  );

  const getStatusIndicatorColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      active: 'bg-success',
      running: 'bg-success',
      draft: 'bg-default-400',
      completed: 'bg-primary',
      finished: 'bg-primary',
      blocked: 'bg-warning',
      cancelled: 'bg-danger'
    };
    return colorMap[status] || 'bg-default-400';
  };

  if (isLoading) {
    return (
      <DefaultLayout>
        <div className="flex items-center justify-center p-8">
          <Spinner size="lg" />
        </div>
      </DefaultLayout>
    );
  }

  if (isError || !experiment) {
    return (
      <DefaultLayout>
        <div className="p-8">
          <Card>
            <CardBody className="p-6">
              <p className="text-danger">Failed to load experiment details</p>
              <Button className="mt-4" onPress={() => navigate('/experiments')}>
                Back to Experiments
              </Button>
            </CardBody>
          </Card>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button isIconOnly variant="light" onPress={() => navigate('/experiments')}>
              <RiArrowLeftLine className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{experiment.name}</h1>
              {experiment.description && (
                <p className="text-default-500 mt-1 text-sm">{experiment.description}</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <ExperimentTypesBadges experiment={experiment} size="sm" />
                <Chip
                  color={getStatusColor(experiment.status)}
                  size="sm"
                  variant="flat"
                  startContent={getStatusIcon(experiment.status)}
                >
                  {getStatusLabel(experiment.status)}
                </Chip>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isBlockableStatus(experiment.status) && (
              <PermissionGuard permission="Experiment.set_status">
                <Button
                  color="danger"
                  variant="flat"
                  onPress={() => setIsBlockModalOpen(true)}
                  startContent={<RiShieldCrossLine className="h-4 w-4" />}
                >
                  Block
                </Button>
              </PermissionGuard>
            )}
            {experiment.status === 'blocked' && (
              <PermissionGuard permission="Experiment.set_status">
                <Button
                  color="success"
                  variant="flat"
                  onPress={() => setIsUnblockModalOpen(true)}
                  startContent={<RiShieldCheckLine className="h-4 w-4" />}
                >
                  Unblock
                </Button>
              </PermissionGuard>
            )}
            <PermissionGuard permission="Experiment.set_status">
              <Button
                variant="flat"
                onPress={() => setIsStatusModalOpen(true)}
                startContent={<RiEdit2Line className="h-4 w-4" />}
              >
                Edit Status
              </Button>
            </PermissionGuard>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Overview */}
          <Card>
            <CardBody className="p-6">
              <h3 className="mb-4 text-lg font-semibold">Overview</h3>
              <div className="space-y-3 text-sm">
                <div className="flex flex-col gap-1">
                  <span className="text-default-500">Type</span>
                  <ExperimentTypesBadges experiment={experiment} />
                </div>
                <div className="flex justify-between">
                  <span className="text-default-500">Domain</span>
                  <DomainCell
                    domain={{
                      id: experiment.domain.id,
                      domain: experiment.domain.domain,
                      title: experiment.domain.title
                    }}
                    showTitle
                    linked
                    showID={false}
                  />
                </div>
                <div className="flex justify-between">
                  <span className="text-default-500">Workspace</span>
                  <OrganizationCell
                    organization={{
                      id: experiment.domain.organization.id,
                      name: experiment.domain.organization.name,
                      desc: null
                    }}
                    showDescription={false}
                    dense
                    linked
                  />
                </div>
                <div className="flex justify-between">
                  <span className="text-default-500">Start Time</span>
                  <span>
                    {experiment.start_time ? (
                      <DateTimeDisplay dateString={experiment.start_time} />
                    ) : (
                      'Not scheduled'
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-default-500">End Time</span>
                  <span>
                    {experiment.end_time ? (
                      <DateTimeDisplay dateString={experiment.end_time} />
                    ) : (
                      'Not scheduled'
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-default-500">Created By</span>
                  <UserCell user={experiment.created_by} dense />
                </div>
                {experiment.updated_by && (
                  <div className="flex justify-between">
                    <span className="text-default-500">Last Updated By</span>
                    <UserCell user={experiment.updated_by} dense />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Metrics */}
          <Card>
            <CardBody className="p-6">
              <h3 className="mb-4 text-lg font-semibold">Metrics</h3>
              <div className="space-y-3 text-sm">
                {experiment.volume_limit != null && (
                  <div className="flex justify-between">
                    <span className="text-default-500">Volume Limit</span>
                    <span>{experiment.volume_limit}</span>
                  </div>
                )}
                {experiment.current_recordings != null && (
                  <div className="flex justify-between">
                    <span className="text-default-500">Current Recordings</span>
                    <span>{experiment.current_recordings}</span>
                  </div>
                )}
                {experiment.sample_size != null && (
                  <div className="flex justify-between">
                    <span className="text-default-500">Sample Size</span>
                    <span>{experiment.sample_size}%</span>
                  </div>
                )}
                {experiment.traffic_allocation != null && (
                  <div className="flex justify-between">
                    <span className="text-default-500">Traffic Allocation</span>
                    <span>{experiment.traffic_allocation}%</span>
                  </div>
                )}
                {experiment.status === 'blocked' && experiment.blocking_reason && (
                  <div className="flex flex-col gap-1">
                    <span className="text-default-500">Blocking Reason</span>
                    <p className="rounded-lg bg-danger-50 p-3 text-danger-700 text-sm dark:bg-danger-950">
                      {experiment.blocking_reason}
                    </p>
                    {experiment.blocking_attempts != null && experiment.blocking_attempts > 0 && (
                      <span className="text-default-500 text-xs">
                        Blocked {experiment.blocking_attempts} time(s)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Cancellation Audit Log - shown when experiment is cancelled */}
        {experiment.status === 'cancelled' && (
          <Card className="border-2 border-danger-200 bg-danger-50">
            <CardBody className="p-6">
              <h3 className="text-danger-700 mb-4 flex items-center gap-2 text-lg font-semibold">
                <RiCloseCircleLine className="h-5 w-5" />
                Cancellation Details
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex flex-col gap-1">
                  <span className="text-default-500">Cancellation Reason</span>
                  <p className="rounded-lg bg-danger-100/50 p-3 text-danger-800 text-sm dark:bg-danger-900/50">
                    {experiment.cancellation_reason?.trim() || 'No reason provided (legacy experiment)'}
                  </p>
                </div>
                {experiment.updated_by && (
                  <div className="flex justify-between">
                    <span className="text-default-500">Cancelled By</span>
                    <UserCell user={experiment.updated_by} dense />
                  </div>
                )}
                {experiment.updated_at && (
                  <div className="flex justify-between">
                    <span className="text-default-500">Cancelled At</span>
                    <DateTimeDisplay dateString={experiment.updated_at} />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Rules & Config */}
        {experiment.config && (
          <Card>
            <CardBody className="p-6">
              <h3 className="mb-4 text-lg font-semibold">Configuration</h3>
              <div className="space-y-4">
                {experiment.config.trackers && experiment.config.trackers.length > 0 && (
                  <div>
                    <h4 className="text-default-500 mb-2 text-xs font-medium uppercase">Trackers</h4>
                    <div className="flex flex-wrap gap-2">
                      {experiment.config.trackers.map((tracker) => (
                        <Chip key={tracker} color="primary" size="sm" variant="flat" className="font-mono text-xs">
                          {tracker} ({getTrackerLabel(tracker)})
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}
                {experiment.config.rules?.click && experiment.config.rules.click.length > 0 && (
                  <div>
                    <h4 className="text-default-500 mb-2 text-xs font-medium uppercase">Click Rules</h4>
                    <div className="flex flex-col gap-2">
                      {experiment.config.rules.click.map((rule, idx) => (
                        <SelectorReadOnlyView
                          key={idx}
                          rule={rule}
                          color="success"
                        />
                      ))}
                    </div>
                  </div>
                )}
                {experiment.config.rules?.impression && experiment.config.rules.impression.length > 0 && (
                  <div>
                    <h4 className="text-default-500 mb-2 text-xs font-medium uppercase">Impression Rules</h4>
                    <div className="flex flex-col gap-2">
                      {experiment.config.rules.impression.map((rule, idx) => (
                        <SelectorReadOnlyView
                          key={idx}
                          rule={rule}
                          color="secondary"
                        />
                      ))}
                    </div>
                  </div>
                )}
                {experiment.config.cookies && experiment.config.cookies.length > 0 && (
                  <div>
                    <h4 className="text-default-500 mb-2 text-xs font-medium uppercase">Cookies</h4>
                    <div className="flex flex-wrap gap-2">
                      {experiment.config.cookies.map((c) => (
                        <Chip key={c} color="warning" size="sm" variant="flat" className="font-mono text-xs">
                          {c}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}
                {experiment.config.stable_attributes && experiment.config.stable_attributes.length > 0 && (
                  <div>
                    <h4 className="text-default-500 mb-2 text-xs font-medium uppercase">Stable Attributes</h4>
                    <div className="flex flex-wrap gap-2">
                      {experiment.config.stable_attributes.map((s) => (
                        <Chip key={s} color="secondary" size="sm" variant="flat" className="font-mono text-xs">
                          {s}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Journeys */}
        {experiment.journeys && (experiment.journeys as JourneyWithSteps[]).length > 0 && (
          <Card>
            <CardBody className="p-6">
              <h3 className="mb-4 text-lg font-semibold">Journeys</h3>
              <div className="space-y-6">
                {(experiment.journeys as JourneyWithSteps[]).map((journey) => (
                  <div key={journey.id} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                    <h4 className="text-default-700 mb-3 font-medium">{journey.name}</h4>
                    <JourneyReadOnlyView journey={journey} />
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Segments */}
        {(experiment.segments ?? experiment.experiment_segments) &&
          ((experiment.segments ?? experiment.experiment_segments) as BackendSegment[]).length > 0 && (
          <Card>
            <CardBody className="p-6">
              <h3 className="mb-4 text-lg font-semibold">Segments</h3>
              <div className="space-y-6">
                {((experiment.segments ?? experiment.experiment_segments) as BackendSegment[]).map((segment) => (
                  <div key={segment.id} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                    <h4 className="text-default-700 mb-3 font-medium">{segment.name}</h4>
                    <SegmentReadOnlyView segment={segment} />
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Extra Settings */}
        {experiment.extra && (
          <Card>
            <CardBody className="p-6">
              <h3 className="mb-4 text-lg font-semibold">Extra Settings</h3>
              <div className="flex flex-wrap gap-3">
                <Chip
                  color={experiment.extra.force_click_tracking ? 'success' : 'default'}
                  size="sm"
                  variant={experiment.extra.force_click_tracking ? 'dot' : 'flat'}
                >
                  Force Click Tracking
                </Chip>
                <Chip
                  color={experiment.extra.capture_all_properties ? 'success' : 'default'}
                  size="sm"
                  variant={experiment.extra.capture_all_properties ? 'dot' : 'flat'}
                >
                  Capture All Properties
                </Chip>
                <Chip
                  color={experiment.extra.referrer_tracking ? 'success' : 'default'}
                  size="sm"
                  variant={experiment.extra.referrer_tracking ? 'dot' : 'flat'}
                >
                  Referrer Tracking
                </Chip>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Danger Zone */}
        {isCancellableStatus(experiment.status) && (
          <Card className="border-2 border-danger-200 bg-danger-50">
            <CardBody className="p-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-danger-700 mb-1 flex items-center gap-2 text-lg font-semibold">
                    <div className="bg-danger h-2 w-2 rounded-full" />
                    Danger Zone
                  </h3>
                  <p className="text-danger-600 text-sm">
                    Cancelling an experiment halts its lifecycle and preserves analytical data. A reason is required.
                  </p>
                </div>
                <PermissionGuard permission="Experiment.set_status">
                  <Button
                    color="danger"
                    variant="flat"
                    onPress={() => setIsCancelModalOpen(true)}
                    startContent={<RiCloseCircleLine className="h-4 w-4" />}
                  >
                    Cancel Experiment
                  </Button>
                </PermissionGuard>
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {experiment && (
        <>
          <BlockExperimentModal
            isOpen={isBlockModalOpen}
            onOpenChange={setIsBlockModalOpen}
            experiment={experiment}
            onSuccess={triggerRefresh}
          />
          <UnblockExperimentModal
            isOpen={isUnblockModalOpen}
            onOpenChange={setIsUnblockModalOpen}
            experiment={experiment}
            onSuccess={triggerRefresh}
          />
          <SetExperimentStatusModal
            isOpen={isStatusModalOpen}
            onOpenChange={setIsStatusModalOpen}
            experiment={experiment}
            onSubmit={handleStatusUpdate}
          />
          <CancelExperimentModal
            isOpen={isCancelModalOpen}
            onOpenChange={setIsCancelModalOpen}
            experiment={experiment}
            onSuccess={triggerRefresh}
          />
        </>
      )}
    </DefaultLayout>
  );
}
